<template xmlns="http://www.w3.org/1999/html">
  <div class="relative">
    <label
      v-if="label"
      :for="textAreaKey"
      class="mb-2 block text-xs font-medium"
      :class="labelClass">{{ label }}</label>
    <textarea
      :id="textAreaKey"
      v-model="modelValue"
      :disabled="disabled"
      :placeholder="placeholder"
      :tabindex="tabIndex"
      type="text"
      :rows="tabRows"
      :maxlength="textAreaMaxLength"
      class="w-full resize-none whitespace-pre-wrap border-none p-0
      hover:cursor-text focus:border-purple-700 focus:ring-purple-700"
      :class="[
        {'hover:bg-gray-200 focus:bg-gray-100': backgroundStyle === 'white'},
        {'hover:bg-black/30 focus:bg-black/60': backgroundStyle === 'black'},
        {'hover:bg-black/10 focus:bg-black/20': backgroundStyle === 'gray'},
        {'bg-gray-200': changed && showButtons},
        {'hover:!cursor-not-allowed hover:!bg-transparent': disabled},
        textAreaClass
      ]"
      @keydown.enter="(e) => (!props.autoGrow && props.tabRows ? e.preventDefault() : '')"
      @focus="(e) => edit(e)"
      @input="(e) => editing(e)"
      @change="(e) => edited(e, false)"
      @keydown.esc="(e) => cancel(e)" />
    <span
      v-if="!showButtons && changed"
      class="absolute -top-1 right-1 text-lg font-bold text-red-500"
      :class="editingIndicatorClass">*</span>
    <div
      v-if="changed && showButtons"
      class="flex justify-end
      text-sm font-semibold"
      :class="buttonsWrapperClass">
      <button
        class="mr-0.5 rounded-bl-md border border-gray-200
        bg-gray-200 px-2 py-1 text-gray-500
        hover:border-gray-300 hover:bg-gray-300 hover:text-gray-700"
        :class="cancelButtonClass"
        @click="cancel">
        Cancel
      </button>
      <button
        class="rounded-br-md border border-purple-800
        bg-purple-800 px-2 py-1 text-white
        hover:border-purple-700 hover:bg-purple-700"
        :class="saveButtonClass"
        @click="save">
        Save changes
      </button>
    </div>
  </div>
</template>

<script setup>
/* external */
import {
  computed,
  onMounted, ref, watch,
} from 'vue';
import { useProposalStore } from '@/Stores/Proposal';

const props = defineProps({
  modelValue: {
    type: [String, null],
    required: true,
    default: '',
  },
  placeholder: {
    type: String,
    required: false,
    default: '',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  labelClass: {
    type: String,
    required: false,
    default: '',
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  showButtons: {
    type: Boolean,
    required: false,
    default: true,
  },
  textAreaMaxLength: {
    type: Number,
    required: false,
    default: null,
  },
  tabIndex: {
    type: Number,
    required: false,
    default: 0,
  },
  tabRows: {
    type: Number,
    required: false,
    default: 0,
  },
  autoGrow: {
    type: Boolean,
    required: false,
    default: true,
  },
  autoSave: {
    type: Boolean,
    required: false,
    default: true,
  },
  cancelChanges: {
    type: Boolean,
    required: false,
    default: false,
  },
  savedChanges: {
    type: Boolean,
    required: false,
    default: false,
  },
  backgroundStyle: {
    type: String,
    required: false,
    default: 'white',
    validator: (value) => ['white', 'black', 'gray'].includes(value),
  },
  textAreaClass: {
    type: String,
    required: false,
    default: '',
  },
  editingIndicatorClass: {
    type: String,
    required: false,
    default: '',
  },
  buttonsWrapperClass: {
    type: String,
    required: false,
    default: '',
  },
  saveButtonClass: {
    type: String,
    required: false,
    default: '',
  },
  cancelButtonClass: {
    type: String,
    required: false,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'save', 'editing', 'edited', 'cancelled']);

// Use Pinia proposal store
const store = useProposalStore();

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const textAreaKey = Math.random().toString(36).substring(2, 15)
  + Math.random().toString(36).substring(2, 15);
const timeOutAutoGrow = ref(0);
const changed = ref(false);
const oldValue = ref(props.modelValue);

/**
 * autogrow textarea
 * @param {HTMLElement} element - textarea element
 */
function autoGrower(element) {
  if (!element || !props.autoGrow) {
    return;
  }
  if (props.tabRows) {
    element.style.height = `${+window.getComputedStyle(element).lineHeight.slice(0, -2) * (props.tabRows + 1)}px`;
  } else {
    element.style.height = '5px';
  }
  if (element.scrollHeight !== element.offsetHeight) {
    element.style.height = `${element.scrollHeight}px`;
  }
}

/**
 * Set oldValue to current value if not changed
 * @param {Event} e - Event object
 */
function edit(e) {
  if (changed.value) {
    return;
  }
  if (props.autoSave) {
    oldValue.value = e.target.value;
  }
}

/**
 * Clear editing timeout, set changed to true and emit edited event data
 * @param {Event} e - Event object
 * @param {boolean} forceSave - Force save changes
 * @returns {Promise<void>}
 */
async function edited(e, forceSave = false) {
  // if forceSave is true or value is different from oldValue, proceed
  if (forceSave === true || oldValue.value !== modelValue.value) {
    changed.value = true;

    // Await the editing timer before emitting the edited event
    await store.setEditingTimer(() => {
      // Emit event if showButtons is false
      if (!props.showButtons) {
        emit('edited', modelValue.value);
      }
    }, 1500);
  }
}

/**
 * autogrow textarea and emit editing event, and finally emit edited event data
 * @param {Event} e - Event object
 */
function editing(e) {
  clearTimeout(timeOutAutoGrow.value);
  timeOutAutoGrow.value = setTimeout(() => {
    autoGrower(e.target);
    changed.value = true;
  }, 100);

  store.setEditingTimer(
    () => {
      emit('editing', true);
      if (!props.showButtons) {
        if (props.autoSave) {
          oldValue.value = e.target.value;
        }
        edited(null, true);
      }
    },
    1500,
  );
}

/**
 * Cancel changes and emit cancelled event data
 * @param {Event} e - Event object
 */
function cancel(e) {
  changed.value = false;
  modelValue.value = oldValue.value;
  if (e) {
    e.target.blur();
  }
  if (props.autoSave) {
    oldValue.value = '';
  }
  emit('cancelled', modelValue.value);
  setTimeout(() => {
    autoGrower(document.getElementById(textAreaKey));
    store.resetEditingTimer();
  }, 1);
}

/**
 *
 */
function save() {
  emit('update:modelValue', modelValue.value);
  emit('save', modelValue.value);
  changed.value = false;
}

onMounted(() => {
  setTimeout(() => {
    autoGrower(document.getElementById(textAreaKey));
  }, 1);
});

watch(() => props.cancelChanges, () => {
  if (changed.value && props.cancelChanges) {
    cancel();
  }
});

watch(() => props.savedChanges, () => {
  if (changed.value && props.savedChanges) {
    changed.value = false;
  }
});

watch(() => props.modelValue, () => {
  clearTimeout(timeOutAutoGrow.value);
  timeOutAutoGrow.value = setTimeout(() => {
    const textAreaElement = document.getElementById(textAreaKey);
    autoGrower(textAreaElement);
  }, 100);
});

</script>
